import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";
import useSeo from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { formatHeat } from "@/utils/format";
import CommentSection from "@/pages/topic-detail/components/CommentSection";

export default function TopicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((t) => t.id === id);

  useSeo({
    title: topic
      ? `${topic.title} - 深夜吃瓜话题讨论区`
      : "话题不存在 - 深夜吃瓜",
    description: topic
      ? `${topic.summary} 进入深夜吃瓜讨论区，查看完整时间线、关键看点，并参与评论区观点互动。`
      : "你访问的话题不存在或已被移除，返回深夜吃瓜话题广场查看更多讨论。",
    keywords: topic
      ? `${topic.title},${topic.tags.join(",")},深夜吃瓜,话题讨论,评论区`
      : "深夜吃瓜,话题广场,讨论区",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            articleSection: topic.categoryName,
            inLanguage: "zh-CN",
            url: `https://ag11k.cn/topics/${topic.id}`,
            image: topic.cover,
            author: { "@type": "Organization", name: topic.author },
            publisher: { "@type": "Organization", name: "深夜吃瓜" },
            keywords: topic.tags.join(","),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://ag11k.cn/" },
              { "@type": "ListItem", position: 2, name: "话题广场", item: "https://ag11k.cn/topics" },
              {
                "@type": "ListItem",
                position: 3,
                name: topic.title,
                item: `https://ag11k.cn/topics/${topic.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-emotion-sad-line text-3xl" />
          </span>
          <h1 className="heading-serif mt-6 text-2xl font-black text-foreground-950">
            这个话题好像已经被撤下了
          </h1>
          <p className="mt-3 max-w-md text-sm text-foreground-500">
            它可能已经被合并到其他讨论区，或者从来没有存在过。去话题广场看看还有哪些热闹的瓜。
          </p>
          <Link
            to="/topics"
            className="mt-7 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <i className="ri-arrow-left-line" />
            返回话题广场
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = topics.filter((t) => t.id !== topic.id && t.category === topic.category);
  const fallback = topics.filter((t) => t.id !== topic.id && t.category !== topic.category);
  const relatedList = [...related, ...fallback].slice(0, 4);

  return (
    <SiteLayout>
      <article>
        <header className="border-b border-background-200/60 bg-background-100/60">
          <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb
              items={[{ label: "话题广场", to: "/topics" }, { label: topic.categoryName }, { label: topic.title }]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link
                to={`/topics?cat=${topic.category}`}
                className="rounded-full bg-primary-500 px-3.5 py-1.5 text-xs font-medium text-background-50 dark:text-foreground-950"
              >
                {topic.categoryName}
              </Link>
              {topic.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-secondary-100/80 px-3 py-1.5 text-xs text-secondary-900"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <h1 className="heading-serif mt-5 max-w-4xl text-3xl font-black leading-snug text-foreground-950 md:text-[42px]">
              {topic.title}
            </h1>

            <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground-500 md:text-lg">
              {topic.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line text-secondary-500" />
                {topic.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {topic.time}
              </span>
              <span className="flex items-center gap-1.5 text-primary-500">
                <i className="ri-fire-line" />
                热度 {formatHeat(topic.heat)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatHeat(topic.views)} 阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line" />
                {topic.comments} 条讨论
              </span>
            </div>
          </div>
        </header>

        <div className="relative h-[260px] w-full overflow-hidden md:h-[420px]">
          <img
            src={topic.cover}
            alt={`${topic.title} - 深夜吃瓜话题配图`}
            title={`${topic.title} - 深夜吃瓜讨论区`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-950/20 to-transparent" />
        </div>

        <div className="mx-auto max-w-content px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="space-y-5">
                {topic.content.map((para) => (
                  <p key={para.slice(0, 12)} className="text-base leading-[1.9] text-foreground-600">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-10 rounded-lg border border-accent-500/30 bg-accent-50/40 p-5 md:p-6">
                <h2 className="heading-serif flex items-center gap-2 text-lg font-bold text-foreground-950">
                  <i className="ri-list-check-2 text-accent-500" />
                  关键看点
                </h2>
                <ul className="mt-4 space-y-3">
                  {topic.points.map((point, index) => (
                    <li key={point} className="flex gap-3 text-sm leading-relaxed text-foreground-600">
                      <span className="brand-font flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xs text-accent-500">
                        {index + 1}
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200/70 bg-background-100/60 p-4">
                <span className="text-sm text-foreground-600">觉得有聊头？</span>
                <a
                  href="#comments"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  <i className="ri-chat-new-line" />
                  去评论区说两句
                </a>
                <Link
                  to="/topics"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-secondary-500 hover:text-secondary-500"
                >
                  <i className="ri-layout-grid-line" />
                  看其他话题
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 lg:space-y-6">
                <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
                  <h2 className="heading-serif text-base font-bold text-foreground-950">话题速览</h2>
                  <dl className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-md bg-background-50 p-3">
                      <dt className="text-xs text-foreground-500">热度</dt>
                      <dd className="brand-font mt-1 text-xl text-primary-500">{formatHeat(topic.heat)}</dd>
                    </div>
                    <div className="rounded-md bg-background-50 p-3">
                      <dt className="text-xs text-foreground-500">阅读</dt>
                      <dd className="brand-font mt-1 text-xl text-foreground-900">{formatHeat(topic.views)}</dd>
                    </div>
                    <div className="rounded-md bg-background-50 p-3">
                      <dt className="text-xs text-foreground-500">讨论</dt>
                      <dd className="brand-font mt-1 text-xl text-secondary-500">{topic.comments}</dd>
                    </div>
                    <div className="rounded-md bg-background-50 p-3">
                      <dt className="text-xs text-foreground-500">看点</dt>
                      <dd className="brand-font mt-1 text-xl text-accent-500">{topic.points.length}</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-background-200/80 bg-background-100 p-5">
                  <h2 className="heading-serif text-base font-bold text-foreground-950">相关话题推荐</h2>
                  <ul className="mt-4 space-y-3">
                    {relatedList.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/topics/${item.id}`}
                          className="group flex gap-3 rounded-md p-2 transition-colors hover:bg-background-50"
                        >
                          <img
                            src={item.cover}
                            alt={`${item.title} - 相关话题推荐`}
                            title={`${item.title} - 深夜吃瓜`}
                            className="h-14 w-20 shrink-0 rounded-md object-cover object-top"
                          />
                          <div className="min-w-0">
                            <p className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-500">
                              {item.title}
                            </p>
                            <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
                              <i className="ri-fire-line text-primary-400" />
                              {formatHeat(item.heat)}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <div className="border-t border-background-200/60 bg-background-100/40">
        <Reveal>
          <CommentSection topicId={topic.id} topicTitle={topic.title} />
        </Reveal>
      </div>
    </SiteLayout>
  );
}